import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { BRAND } from "@/lib/assets";

/**
 * PageShell — used by all inner (non-home) pages.
 * Provides consistent hero band + SEO + spacing.
 */
export default function PageShell({
  title,
  description,
  eyebrow = "",
  heading,
  subheading = "",
  breadcrumbs = [],
  canonical = "",
  children,
  testId = "page-shell",
}) {
  const baseUrl = "https://www.salihigroup.com";
  const canon = canonical || (typeof window !== "undefined" ? baseUrl + window.location.pathname : baseUrl);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canon} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canon} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>

      <section
        data-testid={testId}
        className="relative bg-navy-dark text-white pt-36 pb-16 md:pt-44 md:pb-24 overflow-hidden"
      >
        {/* Watermark logo */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-end pr-[-6rem] opacity-[0.04]" aria-hidden="true">
          <img
            src={BRAND.logo}
            alt=""
            className="w-[900px] max-w-[130%] object-contain brightness-0 invert"
          />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          {breadcrumbs.length > 0 && (
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.22em] text-white/60">
                <li><Link to="/" className="hover:text-gold">Home</Link></li>
                {breadcrumbs.map((c, i) => (
                  <li key={c.label} className="flex gap-2 items-center">
                    <span aria-hidden="true">/</span>
                    {c.to && i < breadcrumbs.length - 1 ? (
                      <Link to={c.to} className="hover:text-gold">{c.label}</Link>
                    ) : (
                      <span className="text-white/90">{c.label}</span>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          )}
          {eyebrow && (
            <div className="flex items-center gap-3 mb-5">
              <span className="gold-hairline" />
              <span className="text-gold text-[11px] uppercase tracking-[0.28em] font-medium">{eyebrow}</span>
            </div>
          )}
          <h1 className="font-serif text-white text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-[-0.01em] max-w-4xl">
            {heading}
          </h1>
          {subheading && (
            <p className="text-white/70 mt-6 text-lg md:text-xl font-light max-w-3xl leading-relaxed">
              {subheading}
            </p>
          )}
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
          {children}
        </div>
      </div>
    </>
  );
}
